/**
 * Clasificador tipológico de infraestructuras PTEL
 * 
 * Implementa detección automática de categorías mediante patrones regex
 * optimizados para nomenclatura andaluza de documentos municipales.
 * 
 * Actualizado con Fase A: Hidráulicas y Energía
 * 
 * @example
 * ```typescript
 * const classifier = new InfrastructureClassifier();
 * const result = classifier.classify("Centro de Salud San Antón");
 * // result.type === InfrastructureType.HEALTH
 * // result.confidence === ClassificationConfidence.HIGH
 * ```
 * 
 * @module services/classification
 */

import type { 
  ClassificationResult,
  ClassifierConfig 
} from '../../types/infrastructure';
import { 
  InfrastructureType, 
  ClassificationConfidence 
} from '../../types/infrastructure';
import { SIGLA_EDUCATIVA_PUNTEADA } from '../../lib/siglasEducativas';

/**
 * Patrón de clasificación con palabras clave y nivel de confianza
 */
interface ClassificationPattern {
  type: InfrastructureType;
  /** Regex principal (alta confianza) */
  primary: RegExp;
  /** Regex secundario (media confianza) */
  secondary?: RegExp;
  /** Palabras clave para debugging */
  keywords: string[];
}

/**
 * Sub-discriminadores in-case EMERGENCY (γ híbrido cluster UPCLS3 eslabón 40).
 * Detectan sub-dominios DERA G12 oleada-2 que comparten semántica de emergencia
 * pero requieren enrutamiento a geocoder específico (SOCORRO=G12_37, HUMANITARIA=G12_36).
 * 
 * Se aplican SOLO si previamente ha matcheado el patrón EMERGENCY, y son aditivos:
 * rellenan el campo `subtype` del `ClassificationResult` sin alterar `type`.
 */
const EMERGENCY_SUBTYPE_PATTERNS: Readonly<Record<'SOCORRO' | 'HUMANITARIA', RegExp>> = {
  SOCORRO: /\b(puesto\s+de\s+socorro|socorrist[ao]s?|lifeguard|\bDYA\b|vigilancia\s+(de\s+)?play[ae]|salvamento\s+(mar[íi]timo|y\s+socorrismo))\b/i,
  HUMANITARIA: /\b(cruz\s+roja|media\s+luna\s+roja|organizaci[óo]n\s+humanitari[ao]|asociaci[óo]n\s+humanitari[ao]|protecci[óo]n\s+civil\s+voluntari[oa]s?)\b/i
};

/**
 * Clasificador tipológico de infraestructuras basado en análisis de 
 * documentos PTEL reales de municipios andaluces.
 * 
 * Patrones calibrados contra nomenclatura oficial de IECA, ISE, DERA, IAPH, REDIAM.
 */
export class InfrastructureClassifier {
  private config: ClassifierConfig;
  private patterns: ClassificationPattern[];

  constructor(config: Partial<ClassifierConfig> = {}) {
    this.config = {
      strictMode: false,
      caseSensitive: false,
      ...config
    };

    this.patterns = this.initializePatterns();
  }

  /**
   * Inicializa patrones de clasificación basados en análisis empírico
   * de 44 documentos PTEL municipales.
   */
  private initializePatterns(): ClassificationPattern[] {
    return [
      // SANITARIOS - 1,500+ infraestructuras en Andalucía
      // FIX #2: Ampliado para nombres descriptivos PTEL "Centro de Salud de [municipio]"
      {
        type: InfrastructureType.HEALTH,
        primary: /\b(centro\s+de\s+salud(\s+de\s+\w+)?|hospital|cl[íi]nica|consultorio(\s+m[ée]dico)?|ambulatorio|urgencias?|centro\s+(sanitario|m[ée]dico)|punto\s+de\s+atenci[óo]n\s+continuada|pac\b|tanatorio|funeraria|morgue)\b/i,
        secondary: /\b(m[ée]dico|sanitari[oa]|sas\b)/i,
        keywords: ['centro salud', 'hospital', 'clínica', 'consultorio', 'ambulatorio', 'urgencias', 'sanitario', 'pac', 'tanatorio', 'funeraria']
      },
      
      // EDUCATIVOS - 3,800+ infraestructuras
      {
        type: InfrastructureType.EDUCATION,
        // SIGLA: las siglas punteadas van FUERA del grupo con \b, porque \b no puede
        // satisfacerse tras un punto literal (ni «.» ni el espacio siguiente son \w):
        // «C.E.I.P. San José» nunca casaba. Se anclan con lookarounds propios y el
        // punto final es opcional para admitir «C.E.I.P» sin punto de cierre.
        // ESPI: la sub-expresión punteada deja de ser literal y pasa a interpolarse
        // desde SIGLA_EDUCATIVA_PUNTEADA, que además admite un espacio tras cada
        // punto («C. E.I.P.», «I. E.S.» — 11 de 73 ocurrencias del corpus). Va en la
        // MISMA ranura de la alternancia: la precedencia entre tipos la fija el orden
        // del array y no se toca. Un solo espacio ([ ]?, no \s*) por diseño de la
        // constante; normalizeName() ya colapsa \s+ a un espacio antes de casar.
        primary: new RegExp(
          `\\b(colegio|instituto|escuela|centro\\s+educativo|ceip|ies|guardería)\\b|${SIGLA_EDUCATIVA_PUNTEADA.source}`,
          'i'
        ),
        secondary: /\b(educaci[óo]n|infantil|primaria|secundaria|aula)\b/i,
        keywords: ['colegio', 'instituto', 'escuela', 'ceip', 'ies', 'guardería', 'educativo']
      },
      
      // PUERTOS - Deportivos, pesqueros, comerciales vía DERA G09 (F2.T9)
      // IMPORTANTE: Debe estar ANTES de DEPORTIVOS y MUNICIPALES para evitar falsos positivos
      {
        type: InfrastructureType.PORT,
        primary: /\b(puerto\s+(deportivo|pesquero|comercial|de\s+pasajeros?|de\s+\w+)|marina\s+(deportiva|náutica|de\s+)|lonja(\s+de)?|dársena|muelle\s+(pesquero|deportivo|comercial)|club\s+náutico|autoridad\s+portuaria|appa)\b/i,
        secondary: /\b(portuari[oa]|embarcadero|varadero|amarr[ea]|pantalán|náutic[oa]|atracadero)\b/i,
        keywords: ['puerto deportivo', 'puerto pesquero', 'marina', 'lonja', 'dársena', 'muelle', 'club náutico', 'portuario', 'embarcadero', 'appa']
      },
      
      // POLICIALES - 200+ infraestructuras
      {
        type: InfrastructureType.POLICE,
        primary: /\b(comisar[íi]a|cuartel\s+de\s+la\s+guardia\s+civil|polic[íi]a\s+(local|nacional)|comandancia|puesto\s+guardia\s+civil)\b/i,
        secondary: /\b(polic[íi]a|guardia\s+civil|g\.civil|seguridad\s+ciudadana)\b/i,
        keywords: ['comisaría', 'cuartel', 'policía', 'guardia civil', 'comandancia']
      },
      
      // BOMBEROS - 86 parques según IECA
      {
        type: InfrastructureType.FIRE,
        primary: /\b(parque\s+de\s+bomberos?|bomberos?|estaci[óo]n\s+de\s+bomberos?)\b/i,
        secondary: /\b(extinc?i[óo]n\s+incendios?|servicios?\s+contra\s+incendios?)\b/i,
        keywords: ['parque bomberos', 'bomberos', 'extinción', 'incendios']
      },
      
      // CULTURALES - 7,000+ vía IAPH
      // CLASSIF-1: Ampliado con patrimonio arqueológico, defensivo y equipamientos municipales
      {
        type: InfrastructureType.CULTURAL,
        primary: /\b(museo|biblioteca|centro\s+cultural|teatro|casa\s+de\s+la\s+cultura|auditorio|necr[óo]polis|atalaya|torre[óo]n|castillo|muralla|sal[óo]n\s+de\s+actos|cementerio|camposanto|plaza\s+de\s+toros)\b/i,
        secondary: /\b(cultural|patrimonio|exposici[óo]n|monumento|ruinas?|yacimiento|arqueol[óo]gic[oa]|fortific|alcazaba)\b/i,
        keywords: ['museo', 'biblioteca', 'centro cultural', 'teatro', 'casa cultura', 'auditorio', 'necrópolis', 'atalaya', 'torreón', 'castillo', 'salón de actos', 'cementerio', 'plaza de toros']
      },
      
      // RELIGIOSOS - 1,500+ lugares culto
      {
        type: InfrastructureType.RELIGIOUS,
        primary: /\b(iglesia|ermita|parroquia|convento|monasterio|catedral|bas[íi]lica|capilla)\b/i,
        secondary: /\b(religios[oa]|culto|templo|sacr[oa])\b/i,
        keywords: ['iglesia', 'ermita', 'parroquia', 'convento', 'monasterio', 'catedral']
      },
      
      // DEPORTIVOS
      // FIX #2: Ampliado para "Piscina Municipal Cubierta", variantes pabellón
      {
        type: InfrastructureType.SPORTS,
        primary: /\b(polideportivo(\s+municipal)?|pabell[óo]n(\s+(deportivo|municipal|cubierto))+|campo\s+de\s+f[úu]tbol|piscina(\s+(municipal|p[úu]blica|cubierta|climatizada|descubierta))+|complejo\s+deportivo|pista\s+(de\s+)?p[áa]del|gimnasio\s+municipal)\b/i,
        secondary: /\b(deportiv[oa]|pista\s+deportiva)\b/i,
        keywords: ['polideportivo', 'pabellón', 'campo fútbol', 'piscina', 'deportivo', 'pádel', 'gimnasio']
      },
      
      // MUNICIPALES
      // FIX #2: Secondary más restrictivo para evitar falsos positivos con "X Municipal"
      {
        type: InfrastructureType.MUNICIPAL,
        primary: /\b(ayuntamiento|casa\s+consistorial|oficinas?\s+(del\s+)?ayuntamiento|oficina\s+municipal|centro\s+administrativo|casa\s+del\s+pueblo|sede\s+municipal)\b/i,
        secondary: /\b(consistorio|servicios?\s+municipales?|dependencias?\s+municipales?)\b/i,
        keywords: ['ayuntamiento', 'consistorial', 'oficina municipal', 'administrativo', 'sede municipal']
      },
      
      // SOCIALES
      {
        type: InfrastructureType.SOCIAL,
        primary: /\b(centro\s+social|residencia|centro\s+de\s+d[íi]a|hogar\s+del\s+pensionista|centro\s+de\s+mayores)\b/i,
        secondary: /\b(social|servicios?\s+sociales?|asistencia\s+social)\b/i,
        keywords: ['centro social', 'residencia', 'centro día', 'hogar pensionista', 'mayores']
      },
      
      // COMBUSTIBLE
      {
        type: InfrastructureType.FUEL,
        primary: /\b(gasolinera|estaci[óo]n\s+de\s+servicio|[áa]rea\s+de\s+servicio|e\.s\.)\b/i,
        secondary: /\b(combustible|carburante|repostaje)\b/i,
        keywords: ['gasolinera', 'estación servicio', 'combustible', 'e.s.']
      },
      
      // MAREMOTO - Zonas de riesgo / equipamiento maremoto-tsunami DERA G12_38 (NUEVO F2 oleada-2 UPCLS3)
      // CRÍTICO: debe estar ANTES de EMERGENCIAS para que "punto de encuentro maremoto" no se quede en EMERGENCY.
      // Decreto 197/2024 · Plan Especial frente al Riesgo de Maremotos.
      {
        type: InfrastructureType.MAREMOTO,
        primary: /\b(maremotos?|tsunamis?|punto\s+de\s+encuentro\s+(de\s+)?(maremotos?|tsunamis?)|zona\s+de\s+evacuaci[óo]n\s+(de\s+)?(maremotos?|tsunamis?)|v[íi]a\s+de\s+evacuaci[óo]n\s+(de\s+)?(maremotos?|tsunamis?))\b/i,
        secondary: /\b(riesgo\s+(de\s+)?(maremoto|tsunami)|evacuaci[óo]n\s+litoral|cota\s+segura)\b/i,
        keywords: ['maremoto', 'tsunami', 'punto encuentro maremoto', 'evacuación maremoto', 'evacuación litoral', 'cota segura']
      },
      
      // EMERGENCIAS
      {
        type: InfrastructureType.EMERGENCY,
        primary: /\b(protecci[óo]n\s+civil|emergencias?|112|centro\s+coordinaci[óo]n|cecopal)\b/i,
        secondary: /\b(emergencia|urgencia|coordinaci[óo]n)\b/i,
        keywords: ['protección civil', 'emergencias', '112', 'cecopal', 'coordinación']
      },
      
      // HIDRÁULICAS - 4,400+ vía REDIAM (NUEVO Fase A)
      // FIX #2: Ampliado para "Depósito de Agua Potable", "Aljibe Municipal"
      {
        type: InfrastructureType.HYDRAULIC,
        primary: /\b(edar|etap|depuradora|potabilizadora|embalse|presa|dep[óo]sito(\s+de)?(\s+agua)?(\s+potable)?|captaci[óo]n(\s+de\s+agua)?|aljibe(\s+municipal)?|balsa\s+de\s+(riego|agua)|fuente\s+p[úu]blica)\b/i,
        secondary: /\b(hidráulic[oa]|saneamiento|abastecimiento|aguas?\s+residuales?)\b/i,
        keywords: ['edar', 'depuradora', 'embalse', 'depósito agua', 'captación', 'potabilizadora', 'etap', 'presa', 'aljibe', 'balsa']
      },
      
      // ENERGÍA - 500+ vía Agencia Andaluza Energía (NUEVO Fase A)
      {
        type: InfrastructureType.ENERGY,
        primary: /\b(subestaci[óo]n|centro\s+de?\s*transformaci[óo]n|central\s+(el[ée]ctrica|t[ée]rmica)|parque\s+e[óo]lico|planta\s+solar|fotovoltaica|l[íi]nea\s+(de\s+)?(alta\s+)?tensi[óo]n)\b/i,
        secondary: /\b(energ[ée]tic[oa]|el[ée]ctric[oa]|aerogenerador|renovable|kv\b|\d+\s*kv)/i,
        keywords: ['subestación', 'centro transformación', 'central eléctrica', 'parque eólico', 'planta solar', 'fotovoltaica', 'línea tensión']
      },
      
      // TELECOMUNICACIONES - Antenas, repetidores, torres (NUEVO Fase B)
      // FIX #2: Ampliado para "Repetidor de Telecomunicaciones Municipal"
      {
        type: InfrastructureType.TELECOM,
        primary: /\b(antena(\s+(de\s+)?(telecomunicaciones?|telefon[íi]a|tv))?|repetidor(\s+(de\s+)?(telecomunicaciones?|comunicaciones?))?(\s+municipal)?|torre\s+(de\s+)?(comunicaci[óo]n|telecomunicaci[óo]n)|estaci[óo]n\s+base|bts|centro\s+(de\s+)?telecomunicaci[óo]n)\b/i,
        secondary: /\b(movistar|vodafone|orange|yoigo|masmovil|telefon[íi]a\s+m[óo]vil|telecom)\b/i,
        keywords: ['antena', 'repetidor', 'torre comunicación', 'estación base', 'telecomunicaciones', 'bts']
      },
      
      // VIAL - Carreteras, caminos (NUEVO Fase B)
      {
        type: InfrastructureType.VIAL,
        primary: /\b(carretera|autov[íi]a|autopista|camino\s+rural|v[íi]a\s+p[ú]blica|acceso\s+principal)\b/i,
        secondary: /\b(vial|viario|carril|tramo|cruce|rotonda|camino)\b/i,
        keywords: ['carretera', 'autovía', 'camino rural', 'vía', 'vial', 'acceso']
      },
      
      // INDUSTRIAL - Fábricas, polígonos (NUEVO Fase B)
      {
        type: InfrastructureType.INDUSTRIAL,
        primary: /\b(pol[íi]gono\s+industrial|nave\s+industrial|f[áa]brica|planta\s+industrial|zona\s+industrial|parque\s+empresarial)\b/i,
        secondary: /\b(industrial|industria|almac[ée]n|taller|manufactura)\b/i,
        keywords: ['polígono industrial', 'nave industrial', 'fábrica', 'zona industrial', 'parque empresarial']
      },
      
      // RSU - Residuos Sólidos Urbanos vía DERA G14 (NUEVO F2.T7)
      {
        type: InfrastructureType.RSU,
        primary: /\b(vertedero|punto\s+limpio|ecoparque|planta\s+(de\s+)?(tratamiento|rsu|residuos|transferencia|compostaje|reciclaje)|centro\s+(de\s+)?reciclaje)\b/i,
        secondary: /\b(residuos?|basura|contenedor\s+soterrado|recogida\s+selectiva|rsu)\b/i,
        keywords: ['vertedero', 'punto limpio', 'ecoparque', 'planta RSU', 'planta tratamiento', 'reciclaje', 'residuos']
      }
    ];
  }

  /**
   * Clasifica una infraestructura basándose en su nombre
   * 
   * @param name - Nombre de la infraestructura (ej: "Centro de Salud La Esperanza")
   * @returns Resultado de clasificación con tipo y nivel de confianza
   * 
   * @example
   * ```typescript
   * classifier.classify("CEIP Miguel Hernández")
   * // { type: 'EDUCATIVO', confidence: 'ALTA', keywords: ['ceip', 'escuela'] }
   * 
   * classifier.classify("EDAR Granada Sur")
   * // { type: 'HIDRAULICO', confidence: 'ALTA', keywords: ['edar', 'depuradora'] }
   * 
   * classifier.classify("Subestación Baza 220kV")
   * // { type: 'ENERGIA', confidence: 'ALTA', keywords: ['subestación', 'kv'] }
   * 
   * classifier.classify("Puerto Deportivo de Almerimar")
   * // { type: 'PUERTO', confidence: 'ALTA', keywords: ['puerto', 'marina'] }
   * ```
   */
  public classify(name: string): ClassificationResult {
    if (!name || name.trim().length === 0) {
      return {
        type: InfrastructureType.GENERIC,
        confidence: ClassificationConfidence.NONE,
        keywords: []
      };
    }

    // Normalizar entrada
    const normalized = this.normalizeName(name);

    // ===== γ híbrido UPCLS3 (eslabón 40) =====
    // Pre-paso: detectar sub-dominios EMERGENCY (SOCORRO, HUMANITARIA) ANTES del barrido genérico.
    // Si matchea SOCORRO o HUMANITARIA, forzar type=EMERGENCY + subtype=... con confianza ALTA.
    // Justificación: keywords "Cruz Roja" o "Puesto de socorro" no necesariamente contienen
    // las palabras del patrón EMERGENCY genérico ("emergencia", "112", "cecopal"), pero
    // semánticamente SON emergencias y deben enrutarse a sus geocoders dedicados (G12_36/G12_37).
    if (EMERGENCY_SUBTYPE_PATTERNS.SOCORRO.test(normalized)) {
      return {
        type: InfrastructureType.EMERGENCY,
        confidence: ClassificationConfidence.HIGH,
        matchedPattern: EMERGENCY_SUBTYPE_PATTERNS.SOCORRO.source,
        keywords: ['puesto socorro', 'socorrista', 'lifeguard', 'DYA', 'vigilancia playa', 'salvamento marítimo'],
        subtype: 'SOCORRO'
      };
    }
    if (EMERGENCY_SUBTYPE_PATTERNS.HUMANITARIA.test(normalized)) {
      return {
        type: InfrastructureType.EMERGENCY,
        confidence: ClassificationConfidence.HIGH,
        matchedPattern: EMERGENCY_SUBTYPE_PATTERNS.HUMANITARIA.source,
        keywords: ['cruz roja', 'media luna roja', 'organización humanitaria', 'protección civil voluntaria'],
        subtype: 'HUMANITARIA'
      };
    }

    // Buscar match con patrones
    for (const pattern of this.patterns) {
      // Intenta match con patrón primario (alta confianza)
      if (pattern.primary.test(normalized)) {
        return {
          type: pattern.type,
          confidence: ClassificationConfidence.HIGH,
          matchedPattern: pattern.primary.source,
          keywords: pattern.keywords
        };
      }

      // Si no está en modo estricto, intenta match secundario (media confianza)
      if (!this.config.strictMode && pattern.secondary?.test(normalized)) {
        return {
          type: pattern.type,
          confidence: ClassificationConfidence.MEDIUM,
          matchedPattern: pattern.secondary.source,
          keywords: pattern.keywords
        };
      }
    }

    // No match - categoría genérica
    return {
      type: InfrastructureType.GENERIC,
      confidence: ClassificationConfidence.NONE,
      keywords: []
    };
  }

  /**
   * Clasifica múltiples infraestructuras en batch
   * 
   * @param names - Array de nombres a clasificar
   * @returns Array de resultados de clasificación
   */
  public classifyBatch(names: string[]): ClassificationResult[] {
    return names.map(name => this.classify(name));
  }

  /**
   * Normaliza nombre de infraestructura para matching
   * - Elimina caracteres especiales superfluos
   * - Normaliza espacios múltiples
   * - Convierte a minúsculas si case-insensitive
   */
  private normalizeName(name: string): string {
    let normalized = name.trim();
    
    // Normalizar espacios múltiples
    normalized = normalized.replace(/\s+/g, ' ');
    
    // Normalizar acentos si es case-insensitive
    if (!this.config.caseSensitive) {
      normalized = normalized.toLowerCase();
    }
    
    return normalized;
  }

  /**
   * Obtiene estadísticas de clasificación para un conjunto de nombres
   * Útil para análisis de datasets PTEL
   * 
   * @param names - Array de nombres a analizar
   * @returns Estadísticas por tipo y nivel de confianza
   */
  public getClassificationStats(names: string[]): Record<string, number> {
    const stats: Record<string, number> = {};
    
    names.forEach(name => {
      const result = this.classify(name);
      const key = `${result.type}_${result.confidence}`;
      stats[key] = (stats[key] || 0) + 1;
    });
    
    return stats;
  }
}
